// LivestreamStatusWave.jsx
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes, css } from 'styled-components';
import { color } from '../style/variables.js';
import media from '../style/media.js';

const LivestreamStatusWave = ({
  width = 12,
  height = 12,
  barColor = color.tealBlue,
  isPlaying = true,
  barQuantity = 3,
}) => {
  // Ensure at least one bar is rendered
  const bars = Math.max(1, barQuantity);

  // Bar dimensions
  const barWidth = 2.5;
  const barHeight = 2;
  const yPosition = 14;
  const animationDelayIncrement = 0.15;

  // SVG dimensions
  const totalWidth = 12;
  const viewBoxWidth = totalWidth;
  const viewBoxHeight = yPosition + barHeight;

  const spacing = useMemo(() => {
    return (totalWidth - bars * barWidth) / (bars + 1);
  }, [totalWidth, bars, barWidth]);

  // default scaleY values for each bar
  const fixedScaleYs = useMemo(() => {
    return Array.from({ length: bars }, (_, index) => {
      function getRandomValue(options) {
        const randomIndex = Math.floor(Math.random() * options.length);
        return options[randomIndex];
      }
      switch (index) {
        case 0:
          return 4;
        case 1:
          return 6;
        case 2:
          return 2;
        default:
          return getRandomValue([2, 4, 6]);
      }
    });
  }, [bars]);

  const rects = useMemo(() => {
    return Array.from({ length: bars }, (_, index) => {
      const x = spacing + index * (barWidth + spacing);
      const fixedScaleY = fixedScaleYs[index];
      return (
        <Bar
          key={index}
          x={x}
          y="14"
          width={barWidth}
          height="7"
          rx="0"
          fillColor={barColor}
          isPlaying={isPlaying}
          delay={index * animationDelayIncrement}
          fixedScaleY={fixedScaleY}
        />
      );
    });
  }, [
    bars,
    spacing,
    barWidth,
    barColor,
    isPlaying,
    animationDelayIncrement,
    fixedScaleYs,
  ]);

  return (
    <StyledSvg
      className="animated-svg"
      width={width}
      height={height}
      viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
    >
      {rects}
    </StyledSvg>
  );
};

LivestreamStatusWave.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  barColor: PropTypes.string,
  isPlaying: PropTypes.bool,
  barQuantity: PropTypes.number,
};

const grow = keyframes`
  0%, 100% {
    transform: scaleY(0.0);
  }
  50% {
    transform: scaleY(6);
  }
`;

const StyledSvg = styled.svg`
  display: block;
  margin: 0 auto;

  width: 16px;
  height: 16px;

  ${media.tablet`
    width: 12px;
    height: 12px;
  `}
`;

const Bar = styled.rect`
  transform-origin: center bottom;
  fill: ${({ fillColor }) => fillColor};
  ${({ isPlaying, delay, fixedScaleY }) =>
    isPlaying
      ? css`
          animation: ${grow} 0.5s ease-in-out infinite;
          animation-delay: ${delay}s;
        `
      : css`
          transform: scaleY(${fixedScaleY});
        `}
`;

export default LivestreamStatusWave;
